'use client'
import { useSelector } from '@xstate/react'
import { useContext } from 'react'

import { LocalConfigContext } from '../context'
import { AuthActorContext } from './AuthProvider'
import { stableAuthStateMatcher } from './WaitForStableAuthState'
import { useLocalStorage } from '@mantine/hooks'
import { OPT_OUT_ONBOARDING_COMPLETION_FLAG, publicRuntimeConfig } from '~/utils'
import { signIn, signOut } from 'next-auth/react'
import { useNavigation } from './NavigationContext'

// NOTE: This state triggers when a new user managed to sign in with nextauth
// but e.g. closed the window without creating a user on the backend
// they could be on any page and we need to redirect such users to /onboarding
// ALL states need to be matched
const NoUserState = ['api.signedOut', 'provider.signedIn', 'registration.unknown']

export function useAppAuth() {
  const { flags } = useContext(LocalConfigContext)
  const navigation = useNavigation()
  // appTrace({ disableonboarding: flags['disableOnboarding'] })

  const { actor: authActor } = useContext(AuthActorContext)
  const isSignedIn = useSelector(authActor, (state) => {
    return state.matches('api.signedIn')
  })

  const needsOnboarding = useSelector(authActor, (state) => {
    if (flags.disableOnboarding) return false
    return state.matches('registration.onboarding') || NoUserState.every((s) => state.matches(s))
  })
  // could be partially created as well
  const isUserCreated = useSelector(authActor, (state) => {
    return state.matches('registration.onboarding') || state.matches('registration.complete')
  })

  const isNonStable = publicRuntimeConfig.deployment !== 'stable'
  const [onboardingCompletionOptedOut, setOnboardingCompletionOptedOut] = useLocalStorage<boolean>({
    key: OPT_OUT_ONBOARDING_COMPLETION_FLAG,
    defaultValue: isNonStable, // true for non-stable, false for stable
  })

  const allowsOnboardingCompletion = useSelector(authActor, (state) => {
    return flags.disableOnboarding ? false : state.matches('registration.complete') && !onboardingCompletionOptedOut
  })

  const isStableAuthState = useSelector(authActor, stableAuthStateMatcher)

  // appTrace('useAppAuth', {
  //   isUserCreated,
  //   isStableAuthState,
  //   isNonStable,
  //   onboardingCompletionOptedOut,
  //   deployment: publicRuntimeConfig.deployment,
  // })

  return {
    handleSignIn: () => {
      navigation.navigate({
        name: 'auth_sign_in',
        route: '/auth/signin',
      })
    },
    handleSignUp: () => signIn(),
    handleSignOut: () => {
      signOut()
      authActor.send({
        type: 'SIGNOUT',
      })
    },
    isSignedIn,
    isUserCreated,
    isStableAuthState,
    allowsOnboardingCompletion,
    needsOnboarding,
    setOnboardingCompletionOptedOut,
  }
}
